import { useEffect, useState, type KeyboardEvent, type RefObject } from "react";

/**
 * SR12 + AX11 — the combobox walk: which option is highlighted, how the arrows
 * move it, and keeping the highlighted row in view.
 *
 * ⚠ RE-IMPLEMENTED, NOT IMPORTED. `molecules/search-panel/search-panel.tsx`
 * lines 607-757 and 952-990 are the reference. That walk is inline in a
 * ~700-line component function and bound to a 304-line module stylesheet, a
 * link-anchor row (its Enter dispatches a click on an `<a>`), an AI variant and
 * a panel that unmounts itself and restores focus to its opener — none of which
 * this component has. Two of its behaviours would also be WRONG here, and are
 * deliberately not carried over:
 *
 *  - `aria-selected={index === activeIndex}` (search-panel.tsx:1016) is the
 *    SINGLE-select idiom, where "selected" means "highlighted". This listbox is
 *    multi-select, so `aria-selected` has to mean CHOSEN, and the highlight is
 *    carried by `aria-activedescendant` alone.
 *  - its `span = rows.length + 1` cycle passes back through -1 at both ends, so
 *    a reader arrows out of the list and into their own typing. SR12 says the
 *    arrows "move between suggestions and wrap around" and gives Esc as the way
 *    back to the field, so -1 here is the pre-entry state, not a cycle member.
 *
 * ⚠ WHY A HOOK AT ALL — not to move a re-render. `activeIndex` is read by the
 * field (`aria-activedescendant`) and by the list, so it correctly lives at
 * their common parent, and extracting it moves the file rather than the render.
 * It is here for the client twin's line budget, and because the walk — the
 * wrap, the two resets, the scroll — is the part worth testing on its own.
 */
export interface ComboboxWalk {
  /** -1 is "no option highlighted" — the state the reader types in. */
  activeIndex: number;
  /** What `aria-activedescendant` points at, or undefined while nothing is. */
  activeOptionId?: string;
  handleKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

export interface ComboboxOptions {
  /** The id prefix every option id is built from. */
  listboxId: string;
  isOpen: boolean;
  /** Every option, including SR5's "select all" row at index 0. */
  optionCount: number;
  /**
   * Changes whenever the result set changes, so the highlight resets.
   *
   * ⚠ CONTENT, NOT THE ARRAY. The ordinary call site rebuilds the array on
   * every render, and keying on its identity wipes the highlight on each
   * keystroke AND each arrow press — unusable by keyboard, and green in a test
   * that only renders once.
   */
  optionsKey: string;
  /** The scroll container holding the options. */
  listRef: RefObject<HTMLElement | null>;
  open: () => void;
  close: () => void;
  /** Choose the option at this index. */
  activate: (index: number) => void;
}

export function comboboxOptionId(listboxId: string, index: number): string {
  return `${listboxId}-option-${index}`;
}

export function useCombobox({
  listboxId,
  isOpen,
  optionCount,
  optionsKey,
  listRef,
  open,
  close,
  activate
}: ComboboxOptions): ComboboxWalk {
  const [activeIndex, setActiveIndex] = useState(-1);

  /* A new set of suggestions is a new list, so the old index would strand the
     highlight on whichever row sits at that position now. */
  useEffect(() => {
    setActiveIndex(-1);
  }, [optionsKey]);

  /* A highlight belongs to a rendered list. Closed — or emptied by the owner
     passing new items — it would otherwise be ready to resume on reopen, and
     one Enter would choose a row nobody pointed at. */
  useEffect(() => {
    if (!isOpen || optionCount === 0) {
      setActiveIndex(-1);
    }
  }, [isOpen, optionCount]);

  /* ⚠ `aria-activedescendant` moves the SELECTION without moving focus, so the
     browser scrolls nothing and only this brings a highlighted row into view
     once the list is past its 25rem cap. `nearest`, so an already-visible row
     does not jolt the list. */
  useEffect(() => {
    if (activeIndex < 0) {
      return;
    }
    listRef.current
      ?.querySelector<HTMLElement>(
        `#${CSS.escape(comboboxOptionId(listboxId, activeIndex))}`
      )
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, listboxId, listRef]);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    /* SR12 — Esc closes from a suggestion, from the select-all row and from
       the field, so it is answered wherever it is pressed inside the search. */
    if (event.key === "Escape") {
      event.preventDefault();
      setActiveIndex(-1);
      close();
      return;
    }

    /* Everything below is the FIELD's keyboard map. The chips and the overflow
       control sit inside the same wrapper this is bound to, and an arrow or an
       Enter aimed at one of those belongs to it. */
    if ((event.target as HTMLElement | null)?.tagName !== "INPUT") {
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      // Arrows would otherwise run the caret to the ends of the typed term.
      event.preventDefault();
      if (!isOpen) {
        open();
      }
      if (optionCount === 0) {
        return;
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex(
        activeIndex < 0 ?
          (step === 1 ? 0 : optionCount - 1) :
          (activeIndex + step + optionCount) % optionCount
      );
      return;
    }

    if (event.key === "Enter" && activeIndex >= 0) {
      /* ⚠ MUST preventDefault. The field is a real `<form role="search">`, so
         an Enter on a highlighted row would also run implicit submission —
         which, with no action and no onSubmit, the atom cancels silently, and
         the defect then reads as "Enter does nothing". */
      event.preventDefault();
      activate(activeIndex);
    }
  };

  return {
    activeIndex,
    activeOptionId:
      activeIndex >= 0 ? comboboxOptionId(listboxId, activeIndex) : undefined,
    handleKeyDown
  };
}
